import Title from '@Components/Title';
import { Notification, NotificationResponse } from '@Reducers/Notifications/Type';
import {
  downloadNotificationReportFile,
  getAllReadNotifications,
  getAllUnreadNotifications,
  putAllNotificationsRead,
  putNotificationReadById,
} from '@Reducers/Notifications/actions';
import { AppDispatch, RootStateType } from '@Store';
import CloseIcon from '@mui/icons-material/Close';
import { TabContext, TabList, TabPanel } from '@mui/lab';
import {
  Box,
  Grow,
  IconButton,
  Paper,
  Stack,
  Tab,
  Typography,
  styled,
  useTheme,
  alpha,
  useMediaQuery,
  Chip,
  Skeleton,
  Tooltip,
} from '@mui/material';
import Drawer from '@mui/material/Drawer';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import React, { useEffect, useState } from 'react';
import InfiniteScroll from 'react-infinite-scroll-component';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { userLogin } from '@Services/hasAdminAccess';
import SbButton from '@Components/Button';
import { setIsDrawerOpen } from '@Reducers/notificationState/notificationState';
import {
  Download as DownloadIcon,
  AccessTime as AccessTimeIcon,
  Label as LabelIcon,
  DoneAll as DoneAllIcon,
  NotificationsNoneOutlined as NotificationsNoneIcon,
  NotificationsActiveOutlined as NotificationsActiveIcon,
  LocalShippingOutlined as LocalShippingIcon,
  ReceiptLongOutlined as ReceiptLongIcon,
  PersonOutline as PersonOutlineIcon,
  AdminPanelSettingsOutlined as AdminPanelSettingsIcon,
  CheckCircleOutline as CheckCircleOutlineIcon,
} from '@mui/icons-material';

dayjs.extend(relativeTime);

interface PropsType {
  icon: React.ReactNode;
}

type TabValues = 'unread' | 'all' | string;

const dateFormat = (apiDate: string) => {
  const currentDate = dayjs();
  const apiDateObject = dayjs(apiDate);

  if (apiDateObject.isSame(currentDate, 'day')) {
    return 'Today';
  } else {
    return `${apiDateObject.format('DD/MM/YYYY')}, ${apiDateObject.format('dddd')}`;
  }
};

// Skeletons for notification list loading
const NotificationListSkeletons = () => (
  <Stack spacing={1.5} sx={{ mt: 1 }}>
    {Array.from({ length: 3 }).map((_, idx) => (
      <Paper
        key={idx}
        elevation={0}
        sx={{
          p: 2,
          borderRadius: '12px',
          border: '1px solid',
          borderColor: 'divider',
        }}
      >
        <Stack spacing={1}>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Skeleton variant="rectangular" width={80} height={20} sx={{ borderRadius: '6px' }} />
            <Skeleton variant="text" width={60} height={16} />
          </Stack>
          <Skeleton variant="text" width="70%" height={20} />
          <Skeleton variant="text" width="95%" height={16} />
          <Skeleton variant="text" width="60%" height={16} />
        </Stack>
      </Paper>
    ))}
  </Stack>
);

function Count({ icon }: PropsType) {
  const dispatch = useDispatch<AppDispatch>();
  const isDrawerOpen = useSelector((state: RootStateType) => state.notificationUI.isDrawerOpen);
  const [readCurrentPage, setReadCurrentPage] = useState(1);
  const [currentPage, setCurrentPage] = useState(1);
  const [tabValue, setTabValue] = useState<TabValues>('unread');
  const { count, notifications, totalReadNotifications, readNotifications, limit } = useSelector(
    (state: RootStateType) => state.notifications,
  );
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));

  const onTabChange = (_e: React.SyntheticEvent, currentTab: TabValues) => {
    setTabValue(currentTab);
  };

  useEffect(() => {
    dispatch(
      getAllUnreadNotifications({
        page: 1,
        limit,
      }),
    );
    dispatch(getAllReadNotifications({ page: 1, limit }));
  }, []);

  const handleOpenNotifications = () => {
    dispatch(setIsDrawerOpen(true));
  };

  useEffect(() => {
    if (readCurrentPage > 1) {
      dispatch(getAllReadNotifications({ page: 1, limit: limit * readCurrentPage }));
    }
  }, [readCurrentPage]);

  useEffect(() => {
    if (currentPage > 1) {
      dispatch(getAllUnreadNotifications({ page: 1, limit: limit * currentPage }));
    }
  }, [currentPage]);

  const unreadNotificationsCount = notifications?.flatMap(data => data?.notifications).length || 0;
  const readNotificationsCount = readNotifications?.flatMap(data => data?.notifications).length || 0;

  return (
    <>
      <TriggerWrapper onClick={handleOpenNotifications}>
        {icon}
        {count > 0 && <span className="count">{count > 99 ? '99+' : count}</span>}
      </TriggerWrapper>

      <Drawer
        anchor="right"
        open={isDrawerOpen}
        onClose={() => {
          dispatch(setIsDrawerOpen(false));
        }}
        variant="temporary"
        ModalProps={{
          keepMounted: true,
        }}
        sx={{
          '& .MuiBackdrop-root': {
            backdropFilter: 'blur(4px)',
            backgroundColor: 'rgba(0,0,0,0.35)',
          },
          '& .MuiDrawer-paper': {
            width: isSmallScreen ? '100%' : '32rem',
            backgroundImage: 'none',
            backdropFilter: 'blur(10px)',
            WebkitBackdropFilter: 'blur(10px)',
            backgroundColor: alpha(theme.palette.background.paper, 0.98),
            boxShadow: '-8px 0px 32px rgba(0, 0, 0, 0.1)',
          },
        }}
      >
        <Box
          sx={{
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          {/* Drawer Top Header */}
          <Box
            sx={{
              p: isSmallScreen ? '1.25rem 1rem 1rem 1rem' : '1.5rem 1.5rem 1rem 2rem',
              borderBottom: '1px solid',
              borderColor: alpha(theme.palette.divider, 0.8),
            }}
          >
            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Stack direction="row" alignItems="center" spacing={1.5}>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: 36,
                    height: 36,
                    borderRadius: '10px',
                    bgcolor: alpha(theme.palette.primary.main, 0.08),
                    color: 'primary.main',
                  }}
                >
                  <NotificationsActiveIcon sx={{ fontSize: 20 }} />
                </Box>
                <Title title="Notifications" removeBorder removePadding />
                {count > 0 && (
                  <Chip
                    size="small"
                    label={`${count} New`}
                    sx={{
                      height: 22,
                      fontSize: '0.72rem',
                      fontWeight: 700,
                      backgroundColor: alpha(theme.palette.secondary.main, 0.1),
                      color: theme.palette.secondary.main,
                      border: `1px solid ${alpha(theme.palette.secondary.main, 0.2)}`,
                    }}
                  />
                )}
              </Stack>
              <Tooltip title="Close">
                <IconButton
                  onClick={() => dispatch(setIsDrawerOpen(false))}
                  size="small"
                  sx={{
                    color: 'text.secondary',
                    '&:hover': {
                      backgroundColor: 'action.hover',
                      color: 'text.primary',
                    },
                  }}
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Stack>
          </Box>

          {/* Tab Navigation and Content */}
          <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
            <TabContext value={tabValue}>
              <Box
                sx={{
                  px: isSmallScreen ? 1.5 : 2.5,
                  pt: 1,
                  borderBottom: '1px solid',
                  borderColor: alpha(theme.palette.divider, 0.5),
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                }}
              >
                <TabList
                  onChange={onTabChange}
                  indicatorColor="primary"
                  TabIndicatorProps={{
                    sx: {
                      height: '3px',
                      borderRadius: '3px 3px 0 0',
                    },
                  }}
                  sx={{
                    minHeight: '44px',
                    '& .MuiTab-root': {
                      textTransform: 'none',
                      fontSize: '0.875rem',
                      fontWeight: 600,
                      minHeight: '44px',
                      minWidth: 'auto',
                      px: 2,
                      color: 'text.secondary',
                      '&.Mui-selected': {
                        color: 'primary.main',
                      },
                    },
                  }}
                >
                  <Tab
                    value="unread"
                    label={
                      <Stack direction="row" alignItems="center" spacing={0.75}>
                        <span>Unread</span>
                        {count > 0 && (
                          <Box
                            sx={{
                              px: 0.75,
                              py: 0.1,
                              borderRadius: '10px',
                              fontSize: '0.7rem',
                              fontWeight: 700,
                              bgcolor: alpha(theme.palette.secondary.main, 0.12),
                              color: theme.palette.secondary.main,
                            }}
                          >
                            {count}
                          </Box>
                        )}
                      </Stack>
                    }
                  />
                  <Tab
                    value="all"
                    label={
                      <Stack direction="row" alignItems="center" spacing={0.75}>
                        <span>All</span>
                        {totalReadNotifications > 0 && (
                          <Box
                            sx={{
                              px: 0.75,
                              py: 0.1,
                              borderRadius: '10px',
                              fontSize: '0.7rem',
                              fontWeight: 600,
                              bgcolor: alpha(theme.palette.text.secondary, 0.1),
                              color: 'text.secondary',
                            }}
                          >
                            {totalReadNotifications}
                          </Box>
                        )}
                      </Stack>
                    }
                  />
                </TabList>

                {tabValue === 'unread' && notifications?.length > 0 && (
                  <Stack direction="row" alignItems="center">
                    <Typography
                      onClick={() => {
                        dispatch(putAllNotificationsRead());
                      }}
                      sx={{
                        fontSize: '0.8rem',
                        fontWeight: 600,
                        color: 'primary.main',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 0.5,
                        px: 1,
                        py: 0.5,
                        borderRadius: '6px',
                        transition: 'all 0.2s',
                        '&:hover': {
                          backgroundColor: alpha(theme.palette.primary.main, 0.08),
                        },
                      }}
                    >
                      <DoneAllIcon sx={{ fontSize: 16 }} />
                      Clear All
                    </Typography>
                  </Stack>
                )}
              </Box>

              {/* Unread Tab Panel */}
              <TabPanel
                value="unread"
                sx={{
                  flex: 1,
                  p: isSmallScreen ? 1.5 : 2,
                  overflowY: 'auto',
                }}
              >
                <InfiniteScroll
                  className="pr-1 fadedScroll"
                  dataLength={unreadNotificationsCount}
                  next={() => {
                    if (count > unreadNotificationsCount) {
                      setCurrentPage(prev => prev + 1);
                    }
                  }}
                  hasMore={count > unreadNotificationsCount}
                  loader={<NotificationListSkeletons />}
                  height={isSmallScreen ? 'calc(100vh - 170px)' : 'calc(100vh - 10px)'}
                  endMessage={
                    count > 0 && count === unreadNotificationsCount ? (
                      <Box sx={{ py: 3, textAlign: 'center' }}>
                        <Chip
                          icon={<CheckCircleOutlineIcon sx={{ fontSize: '15px !important' }} />}
                          label="Yay! You're all caught up"
                          size="small"
                          sx={{
                            backgroundColor: alpha(theme.palette.success.main, 0.08),
                            color: theme.palette.success.main,
                            fontWeight: 600,
                            fontSize: '0.75rem',
                            border: `1px solid ${alpha(theme.palette.success.main, 0.2)}`,
                            '& .MuiChip-icon': { color: 'inherit' },
                          }}
                        />
                      </Box>
                    ) : (
                      <></>
                    )
                  }
                >
                  {notifications?.length > 0 ? (
                    notifications?.map((data: NotificationResponse) =>
                      data?.notifications?.map((notification: Notification) => (
                        <Card key={notification?._id} {...notification} allowClose={true} />
                      )),
                    )
                  ) : (
                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        height: '60vh',
                        textAlign: 'center',
                        p: 3,
                      }}
                    >
                      <Box
                        sx={{
                          width: 56,
                          height: 56,
                          borderRadius: '50%',
                          bgcolor: alpha(theme.palette.primary.main, 0.06),
                          color: 'primary.main',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          mb: 2,
                        }}
                      >
                        <NotificationsNoneIcon sx={{ fontSize: 30 }} />
                      </Box>
                      <Typography
                        variant="subtitle1"
                        sx={{
                          fontWeight: 700,
                          color: 'text.primary',
                          mb: 0.5,
                        }}
                      >
                        No Unread Notifications
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 260 }}>
                        You're completely up to date. New updates and alerts will show up here.
                      </Typography>
                    </Box>
                  )}
                </InfiniteScroll>
              </TabPanel>

              {/* All Tab Panel */}
              <TabPanel
                value="all"
                sx={{
                  flex: 1,
                  p: isSmallScreen ? 1.5 : 2,
                  overflowY: 'auto',
                }}
              >
                <InfiniteScroll
                  className="pr-1 fadedScroll"
                  style={{ overflow: 'hidden', overflowY: 'auto' }}
                  dataLength={readNotificationsCount}
                  next={() => {
                    if (totalReadNotifications > readNotificationsCount) {
                      setReadCurrentPage(prev => prev + 1);
                    }
                  }}
                  hasMore={totalReadNotifications > readNotificationsCount}
                  loader={<NotificationListSkeletons />}
                  height={isSmallScreen ? 'calc(100vh - 170px)' : 'calc(100vh - 180px)'}
                  endMessage={
                    totalReadNotifications > 0 && totalReadNotifications === readNotificationsCount ? (
                      <Box sx={{ py: 3, textAlign: 'center' }}>
                        <Chip
                          icon={<CheckCircleOutlineIcon sx={{ fontSize: '15px !important' }} />}
                          label="Yay! You're all caught up"
                          size="small"
                          sx={{
                            backgroundColor: alpha(theme.palette.success.main, 0.08),
                            color: theme.palette.success.main,
                            fontWeight: 600,
                            fontSize: '0.75rem',
                            border: `1px solid ${alpha(theme.palette.success.main, 0.2)}`,
                            '& .MuiChip-icon': { color: 'inherit' },
                          }}
                        />
                      </Box>
                    ) : (
                      <></>
                    )
                  }
                >
                  {readNotifications?.length > 0 ? (
                    readNotifications?.map((data: NotificationResponse) => (
                      <Box key={data?._id} sx={{ mt: 1.5, mb: 2 }}>
                        <Box
                          sx={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            px: 1.25,
                            py: 0.35,
                            borderRadius: '6px',
                            bgcolor: alpha(theme.palette.primary.main, 0.06),
                            mb: 1.5,
                          }}
                        >
                          <Typography
                            variant="caption"
                            sx={{
                              color: 'primary.dark',
                              fontWeight: 700,
                              fontSize: '0.75rem',
                              letterSpacing: '0.3px',
                            }}
                          >
                            {dateFormat(data?._id)}
                          </Typography>
                        </Box>

                        {data?.notifications?.map((notification: Notification) => (
                          <Card key={notification?._id} {...notification} allowClose={false} />
                        ))}
                      </Box>
                    ))
                  ) : (
                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        height: '60vh',
                        textAlign: 'center',
                        p: 3,
                      }}
                    >
                      <Box
                        sx={{
                          width: 56,
                          height: 56,
                          borderRadius: '50%',
                          bgcolor: alpha(theme.palette.primary.main, 0.06),
                          color: 'primary.main',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          mb: 2,
                        }}
                      >
                        <NotificationsNoneIcon sx={{ fontSize: 30 }} />
                      </Box>
                      <Typography
                        variant="subtitle1"
                        sx={{
                          fontWeight: 700,
                          color: 'text.primary',
                          mb: 0.5,
                        }}
                      >
                        No Notifications Found
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 260 }}>
                        Your notification history will be cataloged here as events occur.
                      </Typography>
                    </Box>
                  )}
                </InfiniteScroll>
              </TabPanel>
            </TabContext>
          </Box>
        </Box>
      </Drawer>
    </>
  );
}

function Card(
  props: Notification & {
    allowClose: boolean;
  },
) {
  const { title, createdAt, message, _id, data, allowClose } = props;
  const dispatch = useDispatch<AppDispatch>();
  const isUser = userLogin();
  const theme = useTheme();
  const [isHovered, setIsHovered] = useState(false);
  const [isExiting, setIsExiting] = useState(false);

  // Extract color based on notification type
  const getTypeColor = () => {
    switch (data?.type) {
      case 'shipment':
        return theme.palette.primary.main;
      case 'invoice':
        return theme.palette.secondary.main;
      case 'user':
        return theme.palette.success.main;
      case 'admin':
        return theme.palette.warning.main;
      default:
        return theme.palette.grey[500];
    }
  };

  // Extract icon based on notification type
  const getTypeIcon = () => {
    switch (data?.type) {
      case 'shipment':
        return <LocalShippingIcon sx={{ fontSize: 13 }} />;
      case 'invoice':
        return <ReceiptLongIcon sx={{ fontSize: 13 }} />;
      case 'user':
        return <PersonOutlineIcon sx={{ fontSize: 13 }} />;
      case 'admin':
        return <AdminPanelSettingsIcon sx={{ fontSize: 13 }} />;
      default:
        return <LabelIcon sx={{ fontSize: 13 }} />;
    }
  };

  const extractShipmentId = (message: string) => {
    const match = message.match(/You have been tagged in a new note on shipment (\d{12})/);
    return match ? match[1] : null;
  };

  const customUrl = () => {
    if (/http/.test(data?.url)) {
      return `${data?.url || ''}`;
    }

    if (title === 'You have been tagged in a new note') {
      return `/shipment/admin/edit/${extractShipmentId(message)}`;
    }
    if (/Status changed for Shipment Number (\d{12})/.test(title)) {
      const match = title.match(/Status changed for Shipment Number (\d{12})/);
      return `/track-shipment?trackingNumber=${match?.[1]}`;
    }
    if (/Shipment (\d{12}) has been cancelled!/.test(title)) {
      const match = title.match(/Shipment (\d{12}) has been cancelled!/);
      return `/track-shipment?trackingNumber=${match?.[1]}`;
    }
    if (/Shipment (\d{12}) Confirmed!/.test(title)) {
      const match = title.match(/Shipment (\d{12}) Confirmed!/);
      return isUser ? `/shipment/history/${match?.[1]}` : `/shipment/admin/edit/${match?.[1]}`;
    }
    if (title === 'Change in shipment charges') {
      const match = message.match(/shipment id (\d{12}) has been changed/);
      return match ? `/shipment/history/${match[1]}` : '';
    }

    if (title === 'New invoice generated.') {
      return '/invoice/list';
    }

    if (title === 'Bulk shipment summary') {
      return '/shipment/bulk-summary';
    }

    if (title === 'Bulk Upload Completed') {
      return '/shipment/bulk-summary';
    }

    if (/Payment for order ID (\d{12}) successfully completed\./.test(title)) {
      const match = title.match(/Payment for order ID (\d{12}) successfully completed\./);
      return `/track-shipment?trackingNumber=${match?.[1]}`;
    }

    if (title === 'Funds successfully deposited to your wallet.') {
      return '/transactions/wallet';
    }

    if (title === 'Credit limited updated') {
      return '/transactions/credit';
    }
    if (title === 'Credit limit exhausted!') {
      return '/transactions/credit';
    }

    if (title === 'Payment received') {
      const match = message.match(/Amount of (\d{12}) received for Order number\/invoice number/);
      return match ? `/shipment/history/${match[1]}` : '';
    }
    if (title === 'New credit note generated.' || title === 'New debit note generated.') {
      return '/invoice/list';
    }

    if (data?.type === 'user' || data?.type === 'admin' || data?.type === 'squad') {
      return `/profile`;
    }
    if (data?.type === 'shipment') {
      return isUser ? `/shipment/history/${data?.url || ''}` : `/shipment/admin/edit/${data?.url || ''}`;
    }
    if (data?.type === 'invoice') {
      return '/invoice/list';
    }
    return '';
  };

  const downloadReportFile = async (reportId: string) => {
    await dispatch(downloadNotificationReportFile(reportId));
  };

  const handleClose = (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    setIsExiting(true);
    // Small delay to allow exit animation
    setTimeout(() => {
      dispatch(putNotificationReadById(_id));
    }, 300);
  };

  // If exiting, don't render
  if (isExiting) return null;

  return (
    <Grow in={!isExiting} timeout={400}>
      <Link to={customUrl()} className="sb-text-black text-decoration-none" style={{ display: 'block' }}>
        <Paper
          elevation={0}
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
          sx={{
            position: 'relative',
            overflow: 'hidden',
            backgroundColor: theme.palette.background.paper,
            borderRadius: '12px',
            mb: 1.5,
            border: '1px solid',
            borderColor: isHovered ? alpha(getTypeColor(), 0.4) : alpha(theme.palette.divider, 0.8),
            boxShadow: isHovered ? `0 6px 20px ${alpha(theme.palette.common.black, 0.06)}` : 'none',
            transform: isHovered ? 'translateY(-2px)' : 'none',
            transition: theme.transitions.create(['all'], {
              duration: theme.transitions.duration.shorter,
            }),
            '&::before': {
              content: '""',
              position: 'absolute',
              left: 0,
              top: 0,
              bottom: 0,
              width: 4,
              backgroundColor: getTypeColor(),
              opacity: isHovered ? 1 : 0.75,
              transition: theme.transitions.create(['width', 'opacity'], {
                duration: theme.transitions.duration.shorter,
              }),
            },
            '&:hover::before': {
              width: 6,
              opacity: 1,
            },
          }}
        >
          <Box sx={{ p: '14px 16px 14px 18px' }}>
            {/* Header Section */}
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                mb: 1,
              }}
            >
              <Stack direction="row" alignItems="center" spacing={1}>
                {data?.type && (
                  <Box
                    sx={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 0.5,
                      backgroundColor: alpha(getTypeColor(), 0.08),
                      color: getTypeColor(),
                      px: 0.9,
                      py: 0.3,
                      borderRadius: '6px',
                      fontSize: '0.7rem',
                      fontWeight: 700,
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px',
                    }}
                  >
                    {getTypeIcon()}
                    <span>{data?.type}</span>
                  </Box>
                )}

                <Stack direction="row" alignItems="center" spacing={0.5}>
                  <AccessTimeIcon sx={{ fontSize: 13, color: 'text.secondary' }} />
                  <Typography
                    variant="caption"
                    sx={{
                      color: 'text.secondary',
                      fontWeight: 400,
                      fontSize: '0.75rem',
                    }}
                  >
                    {dayjs(createdAt)?.fromNow()}
                  </Typography>
                </Stack>
              </Stack>

              {allowClose && (
                <Tooltip title="Mark as read" arrow placement="top">
                  <IconButton
                    size="small"
                    onClick={handleClose}
                    sx={{
                      p: 0.5,
                      color: 'text.secondary',
                      transition: theme.transitions.create(['background-color', 'color'], {
                        duration: theme.transitions.duration.shorter,
                      }),
                      '&:hover': {
                        backgroundColor: alpha(theme.palette.error.main, 0.08),
                        color: theme.palette.error.main,
                      },
                    }}
                  >
                    <CloseIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                </Tooltip>
              )}
            </Box>

            {/* Title Section */}
            <Typography
              variant="subtitle2"
              sx={{
                fontWeight: 600,
                fontSize: '0.88rem',
                mb: 0.5,
                color: 'text.primary',
                lineHeight: 1.35,
                transition: theme.transitions.create('color', {
                  duration: theme.transitions.duration.shorter,
                }),
                ...(isHovered && {
                  color: getTypeColor(),
                }),
              }}
            >
              {title}
            </Typography>

            {/* Message Section */}
            <Typography
              variant="body2"
              component="div"
              sx={{
                color: 'text.secondary',
                fontSize: '0.8rem',
                lineHeight: 1.5,
                '& p': { mb: 0.5 },
                '&:last-child': { mb: 0 },
              }}
              dangerouslySetInnerHTML={{
                __html: message.replace(/\n/g, '<br/>'),
              }}
            />

            {/* Download Button Section */}
            {data?.reportId && (
              <Box sx={{ mt: 1.5, pt: 1, borderTop: '1px solid', borderColor: alpha(theme.palette.divider, 0.5) }}>
                <SbButton
                  variant="outlined"
                  size="small"
                  startIcon={<DownloadIcon sx={{ fontSize: '15px !important' }} />}
                  onClick={e => {
                    e.stopPropagation();
                    e.preventDefault();
                    if (data?.reportId) {
                      downloadReportFile(data?.reportId);
                    }
                  }}
                  sx={{
                    height: '30px',
                    fontSize: '0.75rem',
                    fontWeight: 600,
                    textTransform: 'none',
                    borderColor: alpha(theme.palette.primary.main, 0.3),
                    color: theme.palette.primary.main,
                    borderRadius: '8px',
                    px: 1.5,
                    transition: theme.transitions.create(['background-color', 'border-color', 'transform'], {
                      duration: theme.transitions.duration.shorter,
                    }),
                    '&:hover': {
                      borderColor: theme.palette.primary.main,
                      backgroundColor: alpha(theme.palette.primary.main, 0.05),
                      transform: 'translateY(-1px)',
                    },
                  }}
                >
                  Download Report
                </SbButton>
              </Box>
            )}
          </Box>
        </Paper>
      </Link>
    </Grow>
  );
}

export default Count;

const TriggerWrapper = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  height: '44px',
  width: '44px',
  backgroundColor: '#f4f3f7',
  borderRadius: '12px',
  marginRight: '10px',
  position: 'relative',
  cursor: 'pointer',
  color: '#0f0e47',
  transition: 'background-color 0.2s ease, transform 0.1s ease',
  '&:hover': {
    backgroundColor: '#e8e7ec',
    transform: 'scale(1.02)',
  },
  '&:active': {
    transform: 'scale(0.98)',
  },
  '& .count': {
    position: 'absolute',
    right: '2px',
    bottom: '5px',
    background: theme.palette.secondary.main,
    height: '18px',
    minWidth: '18px',
    padding: '0 4px',
    borderRadius: '999px',
    color: theme.palette.primary.contrastText,
    fontSize: '11px',
    fontWeight: 700,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    boxShadow: '0 2px 5px rgba(0,0,0,0.15)',
  },
}));
